import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Breadcrumb,
  Button,
  Card,
  Tabs,
  Modal,
  Skeleton,
  message,
  Tag,
  Grid,
} from "antd";
import { StarFilled } from "@ant-design/icons";
import photos from "assets/icons/photos.svg";
import Container from "commons/Container";
import { Link, useLocation, useNavigate } from "react-router-dom";
import facebook from "assets/icons/facebook.svg";
import google from "assets/icons/google.svg";
import overview from "assets/icons/overview.svg";
import appointment from "assets/icons/appointment.svg";
import PhysicalTherapyOverview from "../PhysicalTherapyOverview/PhysicalTherapyOverview";
import { Storage } from "aws-amplify";
import PtAppointment from "../Appointment/PtAppointment";
import Chat from "pages/user/SupportChatbox/Chat";

import { GetPublicFileFromS3 } from "utility/uploadFileToS3";
import { GetPhysicalTherapyDetails } from "utility/action";
import { useCallback } from "react";
import ImageGallery from "utility/ImageGallery";
import { END_TOUR, PT_PROFILE_TOUR_START } from "utility/constant";
import { setTourSteps } from "store/actions/tourSteps";
import { useDispatch, useSelector } from "react-redux";
import TourStepContent from "utility/TourStepContent";
import GlobalJoyride from "utility/GlobalJoyride";

export default function PhysicalTherapyProfile() {
  const location = useLocation();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const userID = location?.state?.id;
  const [messageApi, contextHolder] = message.useMessage();
  const [tour, setTour] = useState(false);
  const [tourIndex, setTourIndex] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [ptData, setPTData] = useState([]);
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [preSignedImg, setPreSignedimg] = useState([]);
  const [ptPhoto, setPtPhoto] = useState(null);
  const [activeTab, setActiveTab] = useState("item-1");

  const [testimonialData, setTestimonialData] = useState([
    { testimonialName: "", testimonialRating: "", review: "" },
  ]);
  const tourSteps = useSelector((state) => state.tourReducer?.tourSteps);

  // for chat box
  const [isSupportModalOpen, setSupportIsModalOpen] = useState(false);
  const search = location?.state?.search;

  const showModalSupport = () => {
    setSupportIsModalOpen(true);
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const handleOkSupport = () => {
    setSupportIsModalOpen(false);
  };

  const handleCancelSupport = () => {
    setSupportIsModalOpen(false);
  };

  const fetchptCenter = useCallback(async () => {
    try {
      setSkeletonLoader(true);
      const response = await GetPhysicalTherapyDetails(userID);
      const testimonialData = JSON.parse(response?.testimonial ?? "[]");
      setTestimonialData(testimonialData);
      setPTData(response);
      if (response?.ptProfilePhoto) {
        let ptPhoto;

        if (response?.ptProfilePhoto.startsWith("http")) {
          ptPhoto = response?.ptProfilePhoto;
        } else {
          ptPhoto = await GetPublicFileFromS3(response?.ptProfilePhoto);
        }
        setPtPhoto(ptPhoto);
      }

      if (response?.ptPhotos) {
        const fileKeyy = response?.ptPhotos;
        const urls = [];
        for (const key of fileKeyy) {
          try {
            const url = await Storage.get(key, {
              level: "public",
              expires: 86400,
            });
            urls.push(url);
          } catch (error) {
            console.log(`Error getting pre-signed URL for ${key}: ${error}`);
          }
        }
        setPreSignedimg(urls);
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setSkeletonLoader(false);
      if (tourSteps === PT_PROFILE_TOUR_START) {
        setTour(true);
      }
      setTimeout(() => {
        dispatch(setTourSteps(END_TOUR));
      }, 500);
    }
  }, [userID]);

  useEffect(() => {
    fetchptCenter();
  }, [fetchptCenter]);

  const items = [
    {
      label: (
        <span className="sub-header-font d-flex align-center">
          <img src={overview} className="tabs-icon" />
          {(activeTab === "item-1" && screens.xs) || screens.sm || screens.lg
            ? "Overview"
            : ""}
        </span>
      ),
      key: "item-1",
      children: (
        <PhysicalTherapyOverview data={ptData} loader={skeletonLoader} />
      ),
    },
    {
      label: (
        <span className="sub-header-font d-flex align-center">
          <img src={photos} className="tabs-icon" />
          {(activeTab === "item-2" && screens.xs) || screens.sm || screens.lg
            ? "Photos"
            : ""}
        </span>
      ),
      key: "item-2",
      children: <ImageGallery data={preSignedImg} />,
    },
    {
      label: (
        <span className="sub-header-font d-flex align-center">
          <img src={appointment} className="tabs-icon" />
          {(activeTab === "item-3" && screens.xs) || screens.sm || screens.lg
            ? "Request An Appointment"
            : ""}
        </span>
      ),
      key: "item-3",
      children: <PtAppointment data={ptData} />,
    },
  ];

  const handleEndTour = () => {
    setTour(false);
    navigate("/");
    dispatch(setTourSteps(END_TOUR));
  };

  const serviceSteps = [
    {
      target: ".pt-last-step",
      content: (
        <TourStepContent
          heading="Physical Therapy Center Profile"
          details="Simply click on different tabs to explore all the details related to the Physical Therapy Center."
          numbers={{ currentStep: 6, totalSteps: 6 }}
        />
      ),
      disableBeacon: true,
      spotlightClicks: true,
      placement: "bottom",
    },
  ];

  return (
    <>
      {contextHolder}

      <GlobalJoyride
        steps={serviceSteps}
        run={tour}
        stepIndex={tourIndex}
        setTourIndex={setTourIndex}
      />

      <Container>
        <Breadcrumb className="hide-version-no">
          <Breadcrumb.Item>
            <Link to="/" className="p-font">
              Home
            </Link>
          </Breadcrumb.Item>
          {!search && (
            <Breadcrumb.Item>
              <Link to="/physical-therapy-list" className="p-font">
                Physical Therapy Center List
              </Link>
            </Breadcrumb.Item>
          )}

          <Breadcrumb.Item>
            <span className="p-font">{ptData?.ptName}</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* main content starts*/}

        <Card
          className="doctor-profile pt-last-step mobile-card"
          onClick={handleEndTour}
        >
          <Row>
            {skeletonLoader ? (
              <>
                {" "}
                <Skeleton avatar paragraph={{ rows: 2 }}></Skeleton>{" "}
              </>
            ) : (
              <>
                <Col>
                  <div className="d-flex gap-4">
                    <img src={ptPhoto} alt="" className="doctor-img" />
                    <div className="doctor-detail">
                      <div style={{ display: "flex" }}>
                        <h4 className="doctor-name sub-header-font">
                          {ptData?.ptName}
                        </h4>
                        {/* <h6>Reliant Medical Group (Shrewsbury)</h6> */}
                        {ptData?.ptOccupationalTherapy ? (
                          <Tag
                            className="doctor-name"
                            style={{
                              marginLeft: "10px",
                              display: "flex",
                              alignItems: "center",
                              fontWeight: "bold",
                            }}
                            bordered={false}
                            color="gold"
                          >
                            Offers Occupational Therapy
                          </Tag>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="d-flex gap-3">
                        <p className="p-font">
                          <img src={facebook} alt="facebook Icon" />
                          {ptData?.ptFaceBookRating
                            ? ptData?.ptFaceBookRating
                            : 0}{" "}
                          <StarFilled style={{ color: "#e7a500" }} />
                        </p>

                        <p className="p-font">
                          <img src={google} alt="facebook Icon" />
                          {ptData?.ptGoogleRating
                            ? ptData?.ptGoogleRating
                            : 0}{" "}
                          <StarFilled style={{ color: "#e7a500" }} />
                        </p>
                      </div>
                    </div>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Card>

        <Card className="profile-tabs mobile-card">
          <Row>
            <Col span={24}>
              <Tabs items={items} onChange={handleTabChange} />
            </Col>
          </Row>
        </Card>
      </Container>
      
    
    </>
  );
}
